/**
 * Video Lazy Loading Module
 * 
 * Defers loading of video elements until user interaction (click, scroll, keypress, touch).
 * This improves initial page load performance by not loading videos until needed.
 * 
 * Usage:
 * 1. Add data-src attribute instead of src for <video> elements
 * 2. Add data-poster attribute instead of poster for thumbnail
 * 3. Add class "lazy-video" to the video element
 * 
 * For <source> elements inside <video>:
 * - Use data-src instead of src
 * 
 * Example:
 * <video class="lazy-video" data-poster="/path/to/poster.jpg">
 *   <source data-src="/path/to/video.mp4" type="video/mp4">
 * </video>
 * 
 * For iframes (YouTube, Vimeo):
 * <iframe class="lazy-video" data-src="https://youtube.com/embed/..."></iframe>
 */

// Track if videos have been loaded
let videosLoaded = false;

// User interaction events that trigger video loading
const INTERACTION_EVENTS = [
    'scroll',
    'click', 
    'keypress',
    'touchstart',
    'mousemove'
];

/**
 * Load video by setting src attributes
 * @param {HTMLElement} element - Video or iframe element
 */
function loadVideo(element) {
    // Handle iframe (YouTube, Vimeo embeds)
    if (element.tagName === 'IFRAME') {
        const src = element.dataset.src;
        if (src) {
            element.src = src;
            element.removeAttribute('data-src');
        }
        element.classList.remove('lazy-video');
        element.classList.add('lazy-loaded');
        return;
    }

    // Handle native video element
    if (element.tagName === 'VIDEO') {
        // Set poster if available
        const poster = element.dataset.poster;
        if (poster) {
            element.poster = poster;
            element.removeAttribute('data-poster');
        }

        // Load source elements
        const sources = element.querySelectorAll('source[data-src]');
        sources.forEach(source => {
            source.src = source.dataset.src;
            source.removeAttribute('data-src');
        });

        // Load video src if set directly
        const videoSrc = element.dataset.src;
        if (videoSrc) {
            element.src = videoSrc;
            element.removeAttribute('data-src');
        }

        // Reload video to apply new sources
        if (sources.length > 0 || videoSrc) {
            element.load();
        }

        element.classList.remove('lazy-video');
        element.classList.add('lazy-loaded');
    }
}

/**
 * Load all lazy videos on the page
 */
function loadAllVideos() {
    if (videosLoaded) return;
    
    const lazyVideos = document.querySelectorAll('.lazy-video');
    lazyVideos.forEach(video => {
        loadVideo(video);
    });
    
    videosLoaded = true;
    
    // Remove event listeners after videos are loaded
    removeInteractionListeners();
}

/**
 * Remove all interaction event listeners
 */
function removeInteractionListeners() {
    INTERACTION_EVENTS.forEach(event => {
        window.removeEventListener(event, loadAllVideos, { passive: true });
    });
}

/**
 * Initialize lazy loading for videos
 * Videos load on first user interaction (scroll, click, keypress, touch)
 */
export function initLazyVideos() {
    const lazyVideos = document.querySelectorAll('.lazy-video');
    
    if (lazyVideos.length === 0) {
        return;
    }

    // Add event listeners for user interaction
    INTERACTION_EVENTS.forEach(event => {
        window.addEventListener(event, loadAllVideos, { 
            passive: true,
            once: true // Auto-remove after first trigger
        });
    });
}

/**
 * Lazy load a single video element (for dynamically added content)
 * Loads immediately if user has already interacted, otherwise waits
 * @param {HTMLElement} element - Video or iframe element to lazy load
 */
export function lazyLoadVideo(element) {
    if (!element || !element.classList.contains('lazy-video')) {
        return;
    }

    // If user has already interacted, load immediately
    if (videosLoaded) {
        loadVideo(element);
    }
    // Otherwise it will be loaded when user interacts
}

export default initLazyVideos;
